import { UserProfile } from '../types/user';

/**
 * Field weights for calculating profile completeness
 * Higher weight = more important field
 */
export const PROFILE_FIELD_WEIGHTS: Record<string, number> = {
  // Basic required fields
  displayName: 10,
  photoURL: 8,
  preferredName: 6,

  // Contact information
  phoneNumber: 7,
  contactEmail: 5,

  // Location information
  'location.country': 4,
  'location.region': 3,
  'location.city': 2,
  'location.postalCode': 1,

  // Preferences
  timezone: 2,
  'preferences.theme': 1,
  'preferences.emailNotifications': 1,
  'preferences.newsletterSubscribed': 1,
  'preferences.contentPreferences': 3,
};

/**
 * Gets the total possible profile completeness score
 * @returns Sum of all field weights
 */
export const getTotalProfileScore = (): number => {
  return Object.values(PROFILE_FIELD_WEIGHTS).reduce((sum, weight) => sum + weight, 0);
};

/**
 * Checks if a nested field exists in an object
 * @param obj Object to check
 * @param path Path to field (e.g., 'preferences.theme')
 * @returns True if field exists and has a value
 */
export const hasNestedField = (obj: any, path: string): boolean => {
  const parts = path.split('.');
  let current = obj;

  for (const part of parts) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return false;
    }

    current = current[part];

    if (current === null || current === undefined) {
      return false;
    }

    // Handle arrays - they should have at least one item
    if (Array.isArray(current) && current.length === 0) {
      return false;
    }
  }

  return true;
};

/**
 * Calculates profile completeness as a percentage
 * @param profile User profile
 * @returns Percentage of profile completion (0-100)
 */
export const calculateProfileCompleteness = (profile: UserProfile): number => {
  if (!profile) return 0;

  let score = 0;
  const totalScore = getTotalProfileScore();

  // Calculate score based on field presence
  for (const [field, weight] of Object.entries(PROFILE_FIELD_WEIGHTS)) {
    if (hasNestedField(profile, field)) {
      score += weight;
    }
  }

  // Convert score to percentage
  const percentage = Math.round((score / totalScore) * 100);

  // Ensure percentage is between 0 and 100
  return Math.max(0, Math.min(100, percentage));
};

/**
 * Gets missing profile fields in order of priority (highest weight first)
 * @param profile User profile
 * @returns Array of missing field paths
 */
export const getMissingProfileFields = (profile: UserProfile): string[] => {
  if (!profile) return Object.keys(PROFILE_FIELD_WEIGHTS);

  const missingFields = Object.entries(PROFILE_FIELD_WEIGHTS)
    .filter(([field]) => !hasNestedField(profile, field))
    .sort((a, b) => b[1] - a[1]) // Sort by weight (highest first)
    .map(([field]) => field);

  return missingFields;
};

/**
 * Gets the next recommended field to complete based on priority
 * @param profile User profile
 * @returns Field path or null if profile is complete
 */
export const getNextRecommendedField = (profile: UserProfile): string | null => {
  const missingFields = getMissingProfileFields(profile);
  return missingFields.length > 0 ? missingFields[0] : null;
};

/**
 * Gets a user-friendly label for a profile field path
 * @param fieldPath Path to field (e.g., 'preferences.theme')
 * @returns User-friendly label (e.g., 'Theme Preference')
 */
export const getFieldLabel = (fieldPath: string): string => {
  const fieldLabels: Record<string, string> = {
    displayName: 'Display Name',
    photoURL: 'Profile Photo',
    preferredName: 'Preferred Name',
    phoneNumber: 'Phone Number',
    contactEmail: 'Contact Email',
    'location.country': 'Country',
    'location.region': 'State/Province',
    'location.city': 'City',
    'location.postalCode': 'Postal Code',
    timezone: 'Time Zone',
    'preferences.theme': 'Theme Preference',
    'preferences.emailNotifications': 'Email Notifications',
    'preferences.newsletterSubscribed': 'Newsletter Subscription',
    'preferences.contentPreferences': 'Content Interests'
  };

  return fieldLabels[fieldPath] || fieldPath;
};

/**
 * Gets a placeholder or hint for a profile field
 * @param fieldPath Path to field
 * @returns Placeholder text
 */
export const getFieldPlaceholder = (fieldPath: string): string => {
  const placeholders: Record<string, string> = {
    displayName: 'Enter your full name',
    preferredName: 'What would you like to be called?',
    phoneNumber: 'Enter your phone number',
    contactEmail: 'Enter your preferred contact email',
    'location.country': 'Select your country',
    'location.region': 'Enter your state or province',
    'location.city': 'Enter your city',
    'location.postalCode': 'Enter your postal code',
    timezone: 'Select your timezone',
    'preferences.contentPreferences': 'Select content categories you enjoy'
  };

  return placeholders[fieldPath] || `Enter your ${getFieldLabel(fieldPath).toLowerCase()}`;
};
